import Scene from "~/scene";
import type SceneLayerMarker from "~/scene/layer/marker";
import { store } from "~/state/store";
import { watch, watchAlwaysOnMapItemsCount, watchCoordinates, watchUser } from "~/state/util";
import { Unsubscribe } from "redux";
import { alwaysOnMapLookup } from "~/util/providers/mapybox";
import { CONFIG } from "~/config";
import type { IMarkerOptions } from "~/places/lookup";
import { getImageData, IImageData } from "~/places/imageData";
import { icon, node } from "~/util/dom";
import { getIcon } from "~/util/providers/poiagg";
import { iframeAnalyticsLog } from "~/util/analyticsAction";
import { ELoginState } from "~/state/user/interfaces";
import { IMapState } from "~/state/map/interfaces";
import SceneMarkerFavorite from "~/scene/marker/favorite";

function createSVGIcon(imageData: IImageData): HTMLSpanElement {
	const iconCover = node("span", { className: "mymap-svg-layer" });

	iconCover.appendChild(imageData.svg?.() || icon(imageData.svgName || "star-line"));

	return iconCover;
}

export default class AlwaysOnMap {
	#markerLayer: SceneLayerMarker;

	#markers: Array<{
		data: any;
		marker: SceneMarkerFavorite;
	}>;

	#storeMapUnsubscribe: Unsubscribe;

	#storeUserUnsubscribe: Unsubscribe;

	#storeMymapsUnsubscribe: Unsubscribe;

	#folderId: string;

	/*#detailSource: string;

	#detailId: string | number;*/

	constructor() {
		this.#markers = [];

		const wtMap = watch(store.getState, "map", watchCoordinates);
		const wtUser = watch(store.getState, "user", watchUser);
		const wtMymaps = watch(store.getState, "mymaps", watchAlwaysOnMapItemsCount);
		const wtFolderId = watch<string>(store.getState, "mymaps.openedFolderId");

		// pri zmene mapy udelame lookup
		this.#storeMapUnsubscribe = store.subscribe(wtMap((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				const { user, mymaps } = store.getState();

				// pokud je uzivatel prihlaseny a ma aspon neco videt nad mapou, udelame lookup
				if (user.user.state === ELoginState.Green && mymaps.alwaysOnMapItemsCount > 0) {
					this.#lookup(newMap);
				}
			}
		}));
		// podle stavu prihlaseni uzivatele zapiname a vypiname vrstvu
		this.#storeUserUnsubscribe = store.subscribe(wtUser((newUser, oldUser) => {
			if (newUser.user.state !== oldUser.user.state || newUser.user.rusId !== oldUser.user.rusId) {
				this.#markerLayer.removeAll();
				this.#markers = [];

				if (newUser.user.state === ELoginState.Green) {
					const { mymaps, map } = store.getState();

					// pokud je uzivatel prihlaseny a ma aspon neco videt nad mapou, udelame lookup
					if (mymaps.alwaysOnMapItemsCount > 0) {
						this.#lookup(map);
					}
				}
			}
		}));
		// pokud zacne mit uzivatel neco viditelneho nad mapou, tak udelame lookup
		this.#storeMymapsUnsubscribe = store.subscribe(wtMymaps(newMymaps => {
			if (newMymaps.alwaysOnMapItemsCount > 0) {
				const { user, map } = store.getState();

				// pokud je uzivatel prihlaseny a ma aspon neco videt nad mapou, udelame lookup
				if (user.user.state === ELoginState.Green) {
					this.#lookup(map);
				}
			} else {
				this.#markerLayer.removeAll();
				this.#markers = [];
			}
		}));
		// pokud se otevrela nebo zavrela slozka v MM, je treba aktualizovat, co ma byt vsechno z lookupu videt
		this.#storeMymapsUnsubscribe = store.subscribe(wtFolderId(newFolderId => {
			this.#folderId = newFolderId;
			this.#updateMarkers();
		}));

		// vrstva pro markery veci z MM
		this.#markerLayer = Scene.createLayerMarker();
		Scene.addLayer(this.#markerLayer).enable();

		// napoprve nacteme
		const { map, user, mymaps } = store.getState();

		if (user.user.state === ELoginState.Green && mymaps.alwaysOnMapItemsCount > 0) {
			this.#lookup(map);
		}
	}

	destroy() {
		if (this.#markerLayer) {
			this.#markerLayer.destroy();
			this.#markerLayer = null;
		}

		this.#storeMapUnsubscribe?.();
		this.#storeUserUnsubscribe?.();
		this.#storeMymapsUnsubscribe?.();
	}

	async #lookup(map: IMapState) {
		const viewport = Scene.getViewport();
		const lookupData = await alwaysOnMapLookup(viewport, { zoom: map.zoom });
		const newMarkers = [];

		// projdeme data a co je noveho, to pridame, co uz neni v novych datech, ale existovalo, to odstranime
		lookupData.items?.forEach(item => {
			const oldItem = this.#markers.find(marker => marker.data.id === item.id);

			// uz mame takovy marker
			if (oldItem) {
				newMarkers.push(oldItem);
			// je to novy marker
			} else {
				const marker = this.#createMarker(item);

				if (marker) {
					// rovnou dame do mapy novy marker, pokud ma byt ale zrovna videt
					if (item.parent_id !== this.#folderId) {
						this.#markerLayer.addMarker(marker);
					}

					newMarkers.push({
						data: item,
						marker,
					});
				}
			}
		});

		// projdeme stare markery a ktere nenajdeme v novych, ty dame do ostranovanych
		this.#markers.forEach(marker => {
			// marker uz neni v novych datech
			if (!newMarkers.find(newMarker => newMarker.data.id === marker.data.id)) {
				this.#markerLayer.removeMarker(marker.marker);
			}
		});

		// ulozime si nove markery
		this.#markers = newMarkers;
	}

	// eslint-disable-next-line class-methods-use-this
	#createMarker(item): SceneMarkerFavorite {
		if (item.mark) {
			const coords = Scene.coordsFromWGS84(item.mark.lon, item.mark.lat);
			const opts: IMarkerOptions = {
				type: Scene.VISUAL.MARKER_WINDY.SMALL,
				m3dData: {
					type: "places",
					click: () => {
						this.#openDetail(item);
					},
					onlyMap: true,
				},
				cardClick: () => {
					this.#openDetail(item);
				},
			};
			const imageData = getImageData({
				...item,
				...item.data
					? {
						data: {
							...item.data,
							...item.data.route
								? {
									route: [{ ...item.data.route }],
								}
								: {},
						},
					}
					: {},
			});

			if (item.type === "entity" && item.poi_type_id) {
				const typeId = item.poi_type_id;
				const markerIcon = getIcon(typeId, { scale: 1 });
				let srcset = "";

				if (markerIcon) {
					const icon2x = getIcon(typeId, { scale: 2 });

					if (icon2x) {
						srcset = `${markerIcon} 1x ${icon2x ? `, ${icon2x} 2x` : ""}`;
					}
				}

				opts.icon = markerIcon || undefined;
				opts.srcset = srcset || undefined;
			}

			if (!opts.icon) {
				const svgIcon = createSVGIcon(imageData);

				opts.elm = svgIcon;
				opts.m3dData.svg = imageData.svgName;
			}

			const marker = Scene.createMarkerFavorite(coords, window.structuredClone(item), opts);

			marker.addEventListener("click", () => {
				this.#openDetail(item);
			});

			return marker;
		}

		return null;
	}

	#updateMarkers() {
		this.#markerLayer.removeAll();

		this.#markers.forEach(marker => {
			if (marker.data.parent_id !== this.#folderId) {
				this.#markerLayer.addMarker(marker.marker);
			}
		});
	}

	// eslint-disable-next-line class-methods-use-this
	#openDetail(data) {
		if (CONFIG.inFrame) {
			const state = Mapy.getComponent("history").state;

			iframeAnalyticsLog({
				type: "click-back",
				referrer: document.referrer,
				form: "other",
			});

			window.open(`${CONFIG.mapyURL}/${state.mapset}?dim=${data.id}`);
		} else {
			const Dimcurator = Mapy.getComponent("dimcurator");
			const actualDIMState = Dimcurator.getActualItemState();

			if (actualDIMState?.backAction) {
				actualDIMState.backAction();
			}

			Mapy.getComponent("search").activate();

			Dimcurator.setState({
				dim: data.id,
			}).loadData().then(() => {
				Scene.removeCard();
				Mapy.getComponent("history").save();
			}, () => {
				Scene.removeCard();
			});
		}
	}
}
